"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CarrierNetworkClient = void 0;
const base_client_1 = require("@deliverr/base-client");
const client_auth_1 = require("@deliverr/client-auth");
const commons_objects_1 = require("@deliverr/commons-objects");
const business_types_1 = require("@deliverr/business-types");
const commons_logs_1 = require("@deliverr/commons-logs");
const ResponseUtils_1 = require("./ResponseUtils");
/**
 * Client for interacting with the Carrier Network service
 * Required environment variables:
 * - TRANSPORTATION_SERVICE_URL
 * - One of the following:
 *      - DD_SERVICE
 *      - SERVICE_NAME
 * - ENV: the environment name
 * Optional environment variables:
 * - COGNITO_INTERNAL_CLIENT_ID: the client id for the Cognito user pool
 * - COGNITO_INTERNAL_CLIENT_SECRET: the client secret for the Cognito user pool
 */
class CarrierNetworkClient extends base_client_1.ApiClient {
    constructor(config = {
        authEngine: commons_objects_1.AuthEngine.COGNITO_INTERNAL,
        baseURL: process.env.TRANSPORTATION_SERVICE_URL,
    }, oauthConfig = {}) {
        if (!config.baseURL) {
            commons_logs_1.log.warn("TRANSPORTATION_SERVICE_URL environment variable is required or the baseURL must be provided in the config");
        }
        if (!(config.authEngine === commons_objects_1.AuthEngine.COGNITO_INTERNAL || config.authEngine === commons_objects_1.AuthEngine.COGNITO)) {
            throw new Error("CarrierNetworkClient only supports COGNITO_INTERNAL and COGNITO auth engine");
        }
        const clientTokenBuilder = new client_auth_1.OAuthClientTokenBuilder(oauthConfig);
        super({ ...config, tokenRefreshFn: clientTokenBuilder.getTokenFn() });
    }
    async getNodes(onlyFetchActiveNodes, id) {
        const nodes = await this.get(`/network/v1/node`, { onlyFetchActiveNodes: onlyFetchActiveNodes, id: id });
        return nodes.map((node) => ({
            ...node,
            createdAt: new Date(node.createdAt),
            updatedAt: new Date(node.updatedAt),
        }));
    }
    async getLaneUsage(laneId, trackingDay) {
        return await this.get(`/network/v1/lane/${laneId}/usage`, { trackingDay: trackingDay.toISOString() });
    }
    async registerInternalTrackingUpdate(tracker) {
        const url = `/v1/parcel/internal/tracking-update`;
        try {
            await this.post(url, tracker);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async searchShippingLabels(queryAttributes) {
        const url = `/v1/parcel/labels?`;
        try {
            return await this.get(url, queryAttributes);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [
                business_types_1.ShippingLabelError.TRACKING_CODE_NOT_FOUND,
                business_types_1.ShippingLabelError.NOT_FOUND,
                business_types_1.ShippingLabelError.MISSING_SEARCH_PARAMS,
                business_types_1.ShippingLabelError.DUPLICATE_TRACKING_CODES,
            ]);
        }
    }
    async getShippingLabelById(labelId) {
        const url = `/v1/parcel/labels/${labelId}`;
        try {
            return await this.get(url);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err, [business_types_1.ShippingLabelError.NOT_FOUND]);
        }
    }
    async fetchDocuments(labelId, referenceId, documentType) {
        try {
            return await this.get("/v1/documents", {
                labelId,
                referenceId,
                documentType,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async bulkFetchDocuments(bulkFetchDocumentsRequest) {
        try {
            return await this.post("/v1/documents/bulk", bulkFetchDocumentsRequest);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async generateManifests(generateManifestRequest) {
        try {
            return await this.post("/v2/parcel/manifest", generateManifestRequest);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    /**********************
     * Multi Parcel Rates
     **********************/
    async fetchMultiParcelRates(fetchMultiParcelRatesRequest) {
        try {
            return await this.post("/v1/multi-parcel/rates", fetchMultiParcelRatesRequest);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    /**********************
     * Multi Parcel Labels
     **********************/
    async purchaseNewMultiParcelShippingLabels(purchaseMultiParcelLabelsRequest) {
        try {
            return await this.post("/v1/multi-parcel/labels", purchaseMultiParcelLabelsRequest);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    /**********************
     * Carrier Management
     **********************/
    async fetchCarriers(onlyFetchActiveCarriers = true) {
        try {
            return await this.get("/v1/carrier-management/carrier", { onlyFetchActiveCarriers });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async fetchShippingMethods(onlyFetchActiveShippingMethods = true, includeMetaData = false) {
        try {
            return await this.get("/v1/carrier-management/shippingMethod", {
                onlyFetchActiveShippingMethods,
                includeMetaData,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getHolidaySchedule(carrier) {
        try {
            return await this.get("/v1/carrier-management/holiday/schedule", { carrier });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async cancelShippingLabel(labelId) {
        const url = `/v1/parcel/labels/${labelId}`;
        try {
            return await this.delete(url);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    /**********************
     * Rate Shop Rates
     **********************/
    async fetchRateShopRates(fetchRatesForPackageRequest) {
        try {
            const result = await this.post("/v2/parcel/rates", fetchRatesForPackageRequest);
            // ensure fields of type date are actual date object instances
            const resultWithDeserializedDates = result.map((rate) => {
                const { estimatedShipTime, estimatedDeliveryTime, releaseTime } = rate;
                return {
                    ...rate,
                    estimatedShipTime: (0, ResponseUtils_1.deserializeDateString)(estimatedShipTime),
                    estimatedDeliveryTime: (0, ResponseUtils_1.deserializeDateString)(estimatedDeliveryTime),
                    releaseTime: (0, ResponseUtils_1.deserializeDateString)(releaseTime),
                };
            });
            return resultWithDeserializedDates;
        }
        catch (err) {
            commons_logs_1.log.error({ fn: "CarrierNetworkClient.fetchRateShopRates", err }, "Failed to fetch rate shop rates");
            throw (0, base_client_1.mapError)(err);
        }
    }
    async purchaseNewShippingLabel(request) {
        var _a, _b, _c, _d, _e;
        try {
            const url = "/v1/parcel/labels";
            return await this.post(url, request);
        }
        catch (err) {
            // stop tsc from complaining about the lack of type information
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const error = err;
            throw {
                ...(0, base_client_1.mapError)(err, [
                    business_types_1.ShippingLabelError.INVALID_CARRIER,
                    business_types_1.ShippingLabelError.CARRIER_NOT_FOUND,
                    business_types_1.ShippingLabelError.FAILED_TO_PURCHASE_LABEL,
                ]),
                cause: (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.data,
                subcode: (_c = (_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.subcode,
                payload: (_e = (_d = error === null || error === void 0 ? void 0 : error.response) === null || _d === void 0 ? void 0 : _d.data) === null || _e === void 0 ? void 0 : _e.payload,
            };
        }
    }
    /*
     * Tracking
     */
    async fetchTrackingSummary(trackingCode, carrier, sellerId, lastTrackingEventTime) {
        const url = `/v1/parcel/${carrier}/${trackingCode}/tracking-summary`;
        try {
            return this.get(url, {
                sellerId,
                lastTrackingEventTime,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getTrackingSummaryFromTrackingSummaryTable(trackingCode) {
        const url = `/v1/parcel/tracking-summary/${trackingCode}`;
        try {
            return this.get(url);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getTrackingDetail(trackingCode) {
        const url = `/v1/parcel/tracking-detail/${trackingCode}`;
        try {
            return await this.get(url);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async registerTracker(trackingCode, shippingMethod, deliverrService, labelTracker) {
        const url = `/v1/parcel/register-tracker`;
        try {
            await this.post(url, {
                trackingCode,
                shippingMethod,
                deliverrService,
                labelTracker,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    /**********************
     * Slip Labels
     **********************/
    async appendGiftLabel(giftSlipLabelRequest) {
        var _a, _b, _c;
        try {
            return await this.post("/v1/slips/appendGiftLabel", giftSlipLabelRequest);
        }
        catch (err) {
            // stop tsc from complaining about the lack of type information
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const error = err;
            // don't remove important information from the error if it's already a DeliverrError
            throw {
                ...(0, base_client_1.mapError)(err, ["ORDER_NOT_FOUND" /* SlipLabelError.ORDER_NOT_FOUND */]),
                cause: (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.data,
                message: (_c = (_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message,
            };
        }
    }
    async appendPackingSlipLabel(packingSlipLabelRequest) {
        var _a, _b, _c;
        try {
            return await this.post("/v1/slips/appendPackingSlipLabel", packingSlipLabelRequest);
        }
        catch (err) {
            // stop tsc from complaining about the lack of type information
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const error = err;
            // don't remove important information from the error if it's already a DeliverrError
            throw {
                ...(0, base_client_1.mapError)(err, ["ORDER_NOT_FOUND" /* SlipLabelError.ORDER_NOT_FOUND */, "SHIPMENT_NOT_FOUND" /* SlipLabelError.SHIPMENT_NOT_FOUND */]),
                cause: (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.data,
                message: (_c = (_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message,
            };
        }
    }
    /**********************
     * Batch
     **********************/
    async createBatch(labelRequests) {
        try {
            return await this.post("/v1/parcel/batch", {
                labelRequests,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
}
exports.CarrierNetworkClient = CarrierNetworkClient;
//# sourceMappingURL=CarrierNetworkClient.js.map